import React, { useEffect, useState } from 'react'
import { Typography, withStyles, Tooltip } from '@material-ui/core'
import styles from './styles'
import { Button } from 'src/components/app-ui'
import { Icon } from '@iconify/react'
import { useHistory, useLocation } from 'react-router-dom'
import { useParams } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { domainListAction } from 'src/reducers/domain/action'
import URL from 'src/api/url'
import { patchCourseLandingPageAction } from 'src/reducers/course/action'
import { patchCollectionLandingPageAction } from 'src/reducers/collection/action'
import {
  templateDataCollection,
  templateDataCourse,
  templateDataShowCase,
  withFreshCatalogIds,
} from '../../data'
import {
  LINK_CONTEXTS,
  getCourseLink,
  getPathLink,
  getPortal,
} from 'src/utils/portal-links'
import PortalDomainPicker from './portal-domain-picker'
import LinkContextToggle from '../link-context-toggle'
import usePortal from 'src/hooks/usePortal'
import safeTrack from 'src/utils/safe-track'

const DEFAULT_SHARE_DOMAIN =
  process.env.REACT_APP_VIEW_URL?.replace(/^https?:\/\//, '').replace(
    /\/$/,
    '',
  ) || 'share.minicoursegenerator.com'

const HeaderLandingPage = props => {
  const {
    classes,
    course,
    pageUrl,
    selectedTemplateId,
    linkContext = LINK_CONTEXTS.PORTAL,
    onLinkContextChange,
  } = props
  const dispatch = useDispatch()
  const [copied, setCopied] = useState(false)
  const history = useHistory()
  const location = useLocation()
  const { id, page } = useParams()
  const collection = useSelector(state => state.collection.collection)
  const [domainName, setDomainName] = useState(DEFAULT_SHARE_DOMAIN)
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(true)

  const customDomain = useSelector(state => state.domain.list)
  const customDomainList = customDomain && customDomain.results

  // Get landing page settings
  const courseSettings = useSelector(state => state.course.landingPageSettings)
  const collectionSettings = useSelector(
    state => state.collection.landingPageSettings,
  )
  // The store keeps the last collection opened, which can be the portal while
  // a course is on screen: only a collection page can be the portal.
  const isShowcase = pageUrl === 'collection' && collection?.type === 1
  const me = useSelector(state => state.authentication?.me)
  // The portal belongs to the workspace that owns the path (a collaborator may
  // be in another workspace); courses use the signed-in workspace.
  const portal = usePortal(collection?.shopKey || me?.shopKey)
  const landingPageData =
    pageUrl === 'mini-course' ? courseSettings : collectionSettings
  useEffect(() => {
    const queryDomainList = {
      CurrentPage: 0,
      PageSize: 200,
      SortingColumn: 'LastModifiedAt',
      Asc: false,
      FilterText: '',
      Filters: [],
    }

    if (customDomain.results != null) {
      setLoading(false)
      return
    }
    dispatch(domainListAction(queryDomainList, () => setLoading(false)))
  }, [])

  useEffect(() => {
    if (customDomainList) {
      if (pageUrl === 'collection') {
        const collectionDomain = customDomainList.filter(
          item =>
            item.defaultCollectionDto != null &&
            item.defaultCollectionDto.id == id &&
            item.verificationStatus == 1,
        )
        setDomains(collectionDomain)

        if (collectionDomain.length > 0) {
          setDomainName(collectionDomain[0].domain)
        }
      } else {
        const courseDomain = customDomainList.filter(
          item =>
            item.defaultCourseDto != null &&
            item.defaultCourseDto.id == id &&
            item.verificationStatus == 1,
        )
        setDomains(courseDomain)

        if (courseDomain.length > 0) {
          setDomainName(courseDomain[0].domain)
        }
      }
    }
  }, [customDomainList, id, course?.id, pageUrl])

  const getFullUrl = () => {
    if (isShowcase && portal?.home) return portal.home
    const linkArgs = {
      context: linkContext,
      domain: domainName || URL.APP_URL,
      defaultDomain: DEFAULT_SHARE_DOMAIN,
      portal,
    }
    if (pageUrl === 'collection') {
      return getPathLink({ ...linkArgs, path: collection })
    }
    // A course in a path opens under that path in the portal.
    const inLoadedPath =
      course?.courseCollectionId &&
      String(collection?.id) === String(course.courseCollectionId)
    return getCourseLink({
      ...linkArgs,
      course,
      path: inLoadedPath ? collection : null,
    })
  }
  // The portal can be opened on its custom domain or the default address;
  // the picker switches which one is shown, copied and opened.
  const [portalUrl, setPortalUrl] = useState(null)
  const portalAddresses = isShowcase
    ? [
        portal?.domain && {
          url: portal.home,
          label: portal.domain,
          kind: 'Custom domain',
        },
        {
          url: getPortal({ shopKey: collection?.shopKey || me?.shopKey }).home,
          kind: 'Default',
        },
      ].filter(address => address?.url)
    : []
  const url =
    (isShowcase &&
      portalAddresses.some(address => address.url === portalUrl) &&
      portalUrl) ||
    getFullUrl()

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  }

  // Function to navigate to the desired page after saving
  const navigateAfterSave = () => {
    if (pageUrl === 'mini-course') {
      history.push('/mini-course/' + course.id + '/landing-page/editor')
    } else {
      history.push('/collection/' + id + '/landing-page/editor')
    }
  }

  // Function to apply the selected template
  const applySelectedTemplate = () => {
    // If no template is selected or already initialized, skip this step
    if (
      !selectedTemplateId ||
      selectedTemplateId === 0 ||
      landingPageData?.initialized === true
    ) {
      return false // Return false to indicate no save was needed
    }

    const activeTemplateData =
      pageUrl === 'mini-course'
        ? templateDataCourse
        : isShowcase
        ? templateDataShowCase
        : templateDataCollection
    const selectedTemplateObject = activeTemplateData.find(
      template => template.id === selectedTemplateId,
    )

    if (!selectedTemplateObject) return false // Return false to indicate no save was needed

    const presetStyle = selectedTemplateObject

    const patchAction =
      pageUrl === 'mini-course'
        ? patchCourseLandingPageAction
        : patchCollectionLandingPageAction

    // Dispatch the save action - keep the original structure
    dispatch(
      patchAction(id, [
        {
          path: 'initialized',
          value: true,
        },
        {
          path: 'primaryColor',
          value: presetStyle.primaryColor,
        },
        {
          path: 'font',
          value: presetStyle.font,
        },
        {
          path: 'sections',
          value: withFreshCatalogIds(selectedTemplateObject.value),
        },
      ]),
    )

    return true
  }

  const handleClick = () => {
    const saveTriggered = applySelectedTemplate()
    if (isShowcase) {
      safeTrack('clicked_portal_edit_landing_page', {
        preset_applied: saveTriggered,
        preset_id: saveTriggered ? selectedTemplateId : null,
      })
    }

    if (saveTriggered) {
      setTimeout(() => {
        navigateAfterSave()
      }, 1000)
    } else {
      navigateAfterSave()
    }
  }

  return (
    <div className={classes.content}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.headerText}>
            {isShowcase
              ? 'Landing page'
              : pageUrl === 'collection'
              ? 'Path landing page'
              : 'Course landing page'}
          </Typography>
        </div>

        {/* Link context and URL side by side, centered in the row. */}
        <div className={classes.middle}>
          {!isShowcase && onLinkContextChange && (
            <LinkContextToggle
              value={linkContext}
              onChange={onLinkContextChange}
            />
          )}

          <div className={classes.urlContent}>
            <div className={classes.textContainer}>
              <div className={classes.urlWithIcon}>
                {isShowcase ? (
                  <PortalDomainPicker
                    addresses={portalAddresses}
                    value={url}
                    hasCustomDomain={Boolean(portal?.domain)}
                    onChange={setPortalUrl}
                    onManage={() => history.push('/workspace/custom-domain')}
                  />
                ) : (
                  <Tooltip
                    title={
                      <span>
                        You can customize the default URL by linking it to your
                        own domain on the "
                        <Link
                          to="/workspace/custom-domain"
                          className={classes.link}
                        >
                          Custom Domain
                        </Link>
                        " page.
                      </span>
                    }
                    placement="bottom"
                    arrow
                  >
                    <div className={classes.questionIcon}>
                      <Icon
                        icon="tabler:world"
                        color="#002E47"
                        width="14"
                        height="14"
                      />
                    </div>
                  </Tooltip>
                )}
                <Typography className={classes.urlText}>
                  {url && url}
                </Typography>
              </div>
            </div>
            <div className={classes.copy}>
              <Tooltip title={copied ? 'Link copied' : 'Copy link'} arrow>
                <Icon
                  icon="tabler:copy"
                  width="16px"
                  height="16px"
                  onClick={handleCopy}
                  style={{
                    color: copied ? '#29D25D' : 'var(--Gray-Darker, #838D98)',
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
              <Tooltip title="Open in new tab" arrow>
                <Icon
                  icon="tabler:external-link"
                  width="16px"
                  height="16px"
                  onClick={() => {
                    window.open(url, '_blank')
                  }}
                  style={{
                    cursor: 'pointer',
                    color: 'var(--Gray-Darker, #838D98)',
                  }}
                />
              </Tooltip>
            </div>
          </div>

          {isShowcase && (
            <Button
              size="small"
              variant="outline"
              icon="tabler:world"
              onClick={() => history.push('/workspace/custom-domain')}
            >
              {portal?.domain ? 'Manage domains' : 'Add custom domain'}
            </Button>
          )}
        </div>

        <div>
          <Button
            size="small"
            icon="tabler:pencil"
            onClick={() => handleClick()}
          >
            Edit landing page
          </Button>
        </div>
      </div>
    </div>
  )
}

export default withStyles(styles)(HeaderLandingPage)
